import { useState, useEffect, useRef } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import FileUpload, { getFileUrl } from "@/components/FileUpload";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShoppingCart, Music2, Shirt, Package, Star, Crown, Lock } from "lucide-react";

const PRODUCT_TYPES = [
  { value: "music", label: "Music" },
  { value: "clothing", label: "Clothing" },
  { value: "merch", label: "Merch" },
  { value: "accessories", label: "Accessories" },
  { value: "digital", label: "Digital" },
];

const MUSIC_TYPES = new Set(["music", "single", "ep", "album", "exclusive"]);

const empty = {
  title: "", description: "", price: 0, image_url: "", audio_preview_url: "", download_file: "",
  product_type: "music", artist_name: "", exclusive: false, featured: false, stock: -1,
};

function typeIcon(type) {
  if (MUSIC_TYPES.has(type)) return Music2;
  if (type === "clothing") return Shirt;
  return Package;
}

export default function StoreManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...empty });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch_ = () => api.get("/store/products?admin=true").then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { fetch_(); }, []);

  const openAdd = () => { setEditId(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (item) => { setEditId(item.id); setForm({ ...empty, ...item }); setOpen(true); };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    try {
      if (editId) await api.put(`/store/products/${editId}`, form);
      else await api.post("/store/products", form);
      toast.success(editId ? "Product updated" : "Product added");
      setOpen(false); fetch_();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/store/products/${id}`); toast.success("Deleted"); fetch_(); }
    catch { toast.error("Delete failed"); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isMusic = MUSIC_TYPES.has(form.product_type);

  return (
    <div data-testid="admin-store-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl">STORE</h1>
          <p className="text-white/40 text-sm">{items.length} products</p>
        </div>
        <Button data-testid="add-product-btn" onClick={openAdd} className="bg-[#D4AF37] text-black hover:bg-white text-sm"><Plus className="w-4 h-4 mr-1" /> Add Product</Button>
      </div>

      {loading ? <p className="text-white/30">Loading...</p> : items.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No products yet. Add your first product.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const Icon = typeIcon(item.product_type);
            return (
              <div key={item.id} data-testid={`store-item-${item.id}`} className="bg-[#111111] border border-white/5 p-4 rounded flex items-center gap-4 hover:border-white/10 transition-colors">
                {item.image_url ? (
                  <img src={getFileUrl(item.image_url)} alt="" className="w-14 h-14 object-cover rounded flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 bg-[#1A1A1A] rounded flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-white/20" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold truncate">{item.title}</p>
                    {item.exclusive && <span className="text-[9px] uppercase bg-[#8B0000] text-white px-1.5 py-0.5 rounded font-bold flex-shrink-0">Exclusive</span>}
                    {item.featured && <Star className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-white/40 capitalize">{item.product_type}{item.stock >= 0 ? ` \u00b7 ${item.stock} in stock` : ""}</p>
                </div>
                <span className="text-sm font-heading text-[#D4AF37]">${(item.price || 0).toFixed(2)}</span>
                <Button variant="ghost" size="icon" onClick={() => openEdit(item)} className="text-white/40 hover:text-white"><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editId ? "EDIT PRODUCT" : "ADD PRODUCT"}</DialogTitle>
            <DialogDescription className="text-white/40 text-xs">
              {isMusic ? "Music product — audio upload fields shown." : "Non-music product — standard fields only."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Title + Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Title *</label>
                <input data-testid="product-title-input" value={form.title} onChange={e => setField("title", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Product Type</label>
                <select data-testid="product-type-select" value={form.product_type} onChange={e => setField("product_type", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]">
                  {PRODUCT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            {/* Artist name — music only */}
            {isMusic && (
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Artist Name</label>
                <input value={form.artist_name} onChange={e => setField("artist_name", e.target.value)} placeholder="Luxifer 6" className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20" />
              </div>
            )}

            {/* Description */}
            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Description</label>
              <textarea value={form.description} onChange={e => setField("description", e.target.value)} rows={2} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] resize-none" />
            </div>

            {/* Price + Stock */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Price ($)</label>
                <input type="number" step="0.01" min="0" value={form.price} onChange={e => setField("price", parseFloat(e.target.value) || 0)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Stock <span className="text-white/20">(-1 = unlimited)</span></label>
                <input type="number" min="-1" value={form.stock} onChange={e => setField("stock", parseInt(e.target.value) || -1)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
              </div>
            </div>

            {/* Product Image */}
            <FileUpload label="Product Image" accept="image/*" value={form.image_url} onChange={v => setField("image_url", v)} />

            {/* Audio fields — music only */}
            {isMusic && (
              <>
                <FileUpload label="Audio Preview (30s snippet)" accept="audio/*" value={form.audio_preview_url} onChange={v => setField("audio_preview_url", v)} />
                <FileUpload label="Full Track (download file)" accept="audio/*,.zip" value={form.download_file} onChange={v => setField("download_file", v)} />
              </>
            )}

            {/* Toggles */}
            <div className="flex flex-wrap gap-x-8 gap-y-3 pt-1">
              <div className="flex items-center gap-3">
                <Switch checked={form.featured} onCheckedChange={v => setField("featured", v)} />
                <label className="text-sm text-white/60">Featured</label>
              </div>
              {isMusic && (
                <div className="flex items-center gap-3">
                  <Switch checked={form.exclusive} onCheckedChange={v => setField("exclusive", v)} />
                  <label className="text-sm text-white/60">Exclusive</label>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10 text-white/60">Cancel</Button>
              <Button data-testid="product-save-btn" onClick={handleSave} className="bg-[#D4AF37] text-black hover:bg-white">{editId ? "Update" : "Add"} Product</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
